import { Grid, MenuItem } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { SMBSelect, FiltersButtons, FiltersLayout } from '../../../components';
import { IDropdownResponse } from '../../../models';
import { getBuildCommunicationLots, getBuildCommunicationNeighborhoods } from '../../../fetch';

interface IBuildCommunicationsFiltersProps {
  isLoading: boolean;
  openBuilds: boolean;
  selectedNeighborhoodId: string;
  selectedLotId: string;
  setSelectedNeighborhoodId: (value: string) => void;
  setSelectedLotId: (value: string) => void;
  hasUnreadMessages: string | null;
  setHasUnreadMessages: (value: string | null) => void;
  applyFilters: (clearFilters?: boolean) => void;
}

export const BuildCommunicationsFilters: FC<IBuildCommunicationsFiltersProps> = ({
  isLoading,
  openBuilds,
  selectedNeighborhoodId,
  selectedLotId,
  setSelectedNeighborhoodId,
  setSelectedLotId,
  hasUnreadMessages,
  setHasUnreadMessages,
  applyFilters
}) => {
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);

  const { isLoading: isLoadingNeighborhoods, data: neighborhoods } = useQuery<
    IDropdownResponse[],
    Error
  >(
    ['getBuildCommunicationNeighborhoods', openBuilds],
    () =>
      getBuildCommunicationNeighborhoods({
        restrictToOpenBuilds: openBuilds
      }),
    {
      notifyOnChangeProps: 'tracked'
    }
  );

  const { isLoading: isLoadingLots, data: lots } = useQuery<IDropdownResponse[], Error>(
    ['getBuildCommunicationLots', selectedNeighborhoodId, openBuilds],
    () =>
      getBuildCommunicationLots(selectedNeighborhoodId, {
        restrictToOpenBuilds: openBuilds
      }),
    {
      enabled: !!selectedNeighborhoodId,
      notifyOnChangeProps: 'tracked'
    }
  );

  useEffect(() => {
    // Clear filters when openBuilds changes
    if (selectedNeighborhoodId) {
      setSelectedNeighborhoodId('');
    }
    if (selectedLotId) {
      setSelectedLotId('');
    }
    if (hasUnreadMessages) {
      setHasUnreadMessages(null);
    }
    setHasAppliedFilters(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openBuilds]);

  return (
    <FiltersLayout>
      <Grid item xs={12} sm={6} md={3}>
        <SMBSelect
          label={isLoadingNeighborhoods ? 'Loading...' : 'Neighborhood'}
          disabled={isLoading || isLoadingNeighborhoods}
          value={selectedNeighborhoodId ? selectedNeighborhoodId : ''}
          onChange={e => {
            setSelectedNeighborhoodId(e.target.value);
            // Clear lot selection when neighborhood changes
            if (selectedLotId) {
              setSelectedLotId('');
            }
          }}
          handleClear={() => {
            setSelectedNeighborhoodId('');
            // Clear lot selection when neighborhood is cleared
            if (selectedLotId) {
              setSelectedLotId('');
            }
          }}
        >
          {neighborhoods &&
            !isLoadingNeighborhoods &&
            neighborhoods.map((neighborhood, index) => {
              return (
                <MenuItem key={`${neighborhood.value}-${index}`} value={neighborhood.value}>
                  {neighborhood.description}
                </MenuItem>
              );
            })}
        </SMBSelect>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <SMBSelect
          label={isLoadingLots ? 'Loading...' : 'Lot'}
          disabled={isLoading || isLoadingLots || !selectedNeighborhoodId}
          value={selectedLotId ? selectedLotId : ''}
          onChange={e => {
            setSelectedLotId(e.target.value);
          }}
          handleClear={() => setSelectedLotId('')}
        >
          {lots &&
            !isLoadingLots &&
            lots.map((lot, index) => {
              return (
                <MenuItem key={`${lot.value}-${index}`} value={lot.value}>
                  {lot.description}
                </MenuItem>
              );
            })}
        </SMBSelect>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <SMBSelect
          label="Unread Messages"
          disabled={isLoading}
          value={hasUnreadMessages || ''}
          onChange={e => {
            setHasUnreadMessages(
              e.target.value === 'true' ? 'true' : e.target.value === 'false' ? 'false' : null
            );
          }}
          handleClear={() => setHasUnreadMessages(null)}
        >
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </SMBSelect>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FiltersButtons
          hasAppliedFilters={hasAppliedFilters}
          isDisabled={isLoading}
          handleApplyFilters={() => {
            setHasAppliedFilters(true);
            applyFilters();
          }}
          handleResetFilters={() => {
            setHasAppliedFilters(false);
            setSelectedNeighborhoodId('');
            setSelectedLotId('');
            setHasUnreadMessages(null);
            applyFilters(true);
          }}
        />
      </Grid>
    </FiltersLayout>
  );
};
